'use client';

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useForm, Controller } from "react-hook-form";
import { Pencil, AlertCircle, Building2, User } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { useLab } from "../../_helper/PharmacyProvider";
import { updatUser, viewUser } from "./service/user-service";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { cityMaster, districtMaster, stateMaster } from "../LabProfile/service/profile-service";
import S3ImageLoader from "../../S3ImageLoader";
import errorProfile from "../../assets/images/blank_profile.png";
import { useTranslation } from "react-i18next";

export default function EditUser() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [profileImage, setProfileImage] = useState(null);
    const [imageSource, setImageSource] = useState(errorProfile);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [imageError, setImageError] = useState("");
    const { currentLab } = useLab();
    const { setLoading } = useLoader();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        country: "IN",
        state: "",
        district: "",
        city: "",
        pincode: ""
    });

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocation, setLoadingLocation] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitting },
    } = useForm();

    // Fetch user and prefill
    useEffect(() => {
        const fetchUser = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const res = await viewUser(id);
                if (res.data.success && res.data.data) {
                    const user = res.data.data;
                    const cp = user.contact_person || {};

                    // Profile image
                    if (cp.profile_picture || cp.profilePicture) {
                        setProfileImage(cp.profile_picture || cp.profilePicture);
                    }

                    // Basic fields
                    reset({
                        firstName: cp.first_name || "",
                        lastName: cp.last_name || "",
                        email: cp.contact_email || "",
                        mobile: cp.mobile || "",
                        address: cp.address || "",
                    });

                    // Country logic
                    const isIndia = cp.country === "India" || cp.country === "IN";
                    const countryCode = isIndia ? "IN" : "OTHER";

                    // Temporary hold values
                    const preValues = {
                        state: cp.state || "",
                        district: cp.district || "",
                        city: cp.city || "",
                        pincode: cp.pin_code || cp.pincode || ""
                    };

                    // Set country first
                    setFormData(prev => ({
                        ...prev,
                        country: countryCode,
                        state: "",
                        district: "",
                        city: "",
                        pincode: ""
                    }));
                }
            } catch (error) {
                toast.warning(t("Failed to load user data"));
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id, reset, setLoading, t]);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setImageError(t("File size must be less than 5MB"));
            return;
        }
        if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
            setImageError(t("Only JPG, PNG, or GIF files are allowed"));
            return;
        }

        setImageError("");
        setProfileImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setProfileImage(reader.result);
        reader.readAsDataURL(file);
    };

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const formDataSubmit = new FormData();
            formDataSubmit.append("first_name", data.firstName);
            formDataSubmit.append("last_name", data.lastName);
            formDataSubmit.append("contact_email", data.email);
            formDataSubmit.append("mobile", data.mobile);
            formDataSubmit.append("address", data.address || "");
            formDataSubmit.append("country", formData.country === "IN" ? "India" : data.country || "");
            formDataSubmit.append("state", formData.state || "");
            formDataSubmit.append("district", formData.district || "");
            formDataSubmit.append("city", formData.city || "");
            formDataSubmit.append("pincode", formData.pincode || "");

            if (profileImageFile) {
                formDataSubmit.append("profilePicture", profileImageFile);
            }

            const res = await updatUser(id, formDataSubmit);
            if (res.data.success) {
                toast.success(res.data.message || t("User updated successfully"));
                navigate(`${process.env.PUBLIC_URL}/user`);
            }
        } catch (error) {
            console.error(error);
            toast.warning(t("Failed to update user"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Breadcrumbs mainTitle={t("Update User")} parent={t("Home")} subParent={t("List Of User")} title={t("Update User")} />

            <Container fluid={true}>
                <Row>
                    <Col>
                        <Card className="tw-shadow-sm tw-rounded-xl tw-border-0 tw-bg-white tw-p-4 md:tw-p-6">
                            <form className="tw-space-y-4" onSubmit={handleSubmit(onSubmit)}>
                                {/* Form Fields: Responsive Grid First */}
                                {/* Desktop: 3 columns | Tablet: 2 columns | Mobile: 1 column */}
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3.5">
                                    <div>
                                        <label className="tw-block tw-font-medium tw-text-sm tw-mb-1">{t("First Name")} <span className="tw-text-red-500">*</span></label>
                                        <input type="text" className="form-control" placeholder={t("Enter first name")} {...register("firstName", { required: t("First name is required") })} />
                                        {errors.firstName && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.firstName.message}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-block tw-font-medium tw-text-sm tw-mb-1">{t("Last Name")} <span className="tw-text-red-500">*</span></label>
                                        <input type="text" className="form-control" placeholder={t("Enter last name")} {...register("lastName", { required: t("Last name is required") })} />
                                        {errors.lastName && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.lastName.message}</p>}
                                    </div>
                                    <div className="sm:tw-col-span-2 lg:tw-col-span-1">
                                        <label className="tw-block tw-font-medium tw-text-sm tw-mb-1">{t("Email")} <span className="tw-text-red-500">*</span></label>
                                        <input type="email" className="form-control" placeholder={t("Enter email")} {...register("email", { required: t("Email is required") })} readOnly />
                                        {errors.email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email.message}</p>}
                                    </div>
                                </div>

                                {/* Followed by 2 columns */}
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3.5">
                                    <div>
                                        <label className="tw-block tw-font-medium tw-text-sm tw-mb-1">{t("Mobile Number")} <span className="tw-text-red-500">*</span></label>
                                        <Controller
                                            name="mobile"
                                            control={control}
                                            rules={{ required: t("Mobile is required") }}
                                            render={({ field }) => (
                                                <PhoneInput
                                                    {...field}
                                                    country="in"
                                                    enableSearch
                                                    placeholder="+91 9876543210"
                                                    inputClass="form-control"
                                                    inputStyle={{ width: "100%" }}
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                        {errors.mobile && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.mobile.message}</p>}
                                    </div>
                                    {formData.country === "OTHER" && (
                                        <div>
                                            <label className="tw-block tw-font-medium tw-text-sm tw-mb-1">{t("Country Name")}</label>
                                            <input type="text" className="form-control" placeholder={t("Enter country")} {...register("country")} />
                                        </div>
                                    )}
                                </div>

                                {/* Dedicated Section Below Inputs: Profile Image & Laboratory Info side by side */}
                                <div className="tw-pt-3.5 tw-border-t tw-border-gray-200">
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-items-center">
                                        {/* Profile Image - Moderately Sized */}
                                        <div className="tw-flex tw-items-center tw-gap-4">
                                            <div className="tw-relative tw-flex-shrink-0">
                                                <label htmlFor="profile-upload" className="tw-cursor-pointer tw-block tw-m-0">
                                                    <div className="tw-w-24 tw-h-24 tw-rounded-full tw-overflow-hidden tw-bg-gray-100 tw-border-2 tw-border-dashed tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-border-primary tw-transition-all">
                                                        {profileImage ? (
                                                            <S3ImageLoader
                                                                fileName={profileImage}
                                                                fallbackSrc={errorProfile}
                                                                className="tw-w-full tw-h-full tw-object-cover"
                                                                alt={t("Profile")}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <User className="tw-w-10 tw-h-10 tw-text-gray-400" />
                                                        )}
                                                    </div>
                                                </label>
                                                <label htmlFor="profile-upload" className="tw-absolute tw-bottom-0.5 tw-right-0.5 bg-primary tw-rounded-full tw-p-1.5 tw-shadow-sm tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-border-2 tw-border-white tw-m-0">
                                                    <Pencil className="tw-w-3.5 tw-h-3.5 tw-text-white" />
                                                </label>
                                                <input
                                                    id="profile-upload"
                                                    type="file"
                                                    accept="image/jpeg,image/jpg,image/png,image/gif"
                                                    onChange={handleImageUpload}
                                                    className="tw-hidden"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="profile-upload" className="tw-cursor-pointer tw-block tw-font-semibold tw-text-sm tw-text-gray-800 tw-mb-0.5">
                                                    {t("Profile Picture")}
                                                </label>
                                                <p className="tw-text-gray-500 tw-text-xs tw-mb-0">{t("JPG, PNG or GIF (max 5MB)")}</p>
                                                {imageError && (
                                                    <div className="tw-flex tw-items-center tw-gap-1 tw-text-red-600 tw-text-xs tw-mt-1">
                                                        <AlertCircle className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" />
                                                        <span>{imageError}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Laboratory Info */}
                                        <div className="tw-w-full tw-rounded-lg tw-p-3 tw-px-3.5 tw-bg-gray-50/70" style={{ border: "1px solid #1b255b" }}>
                                            <div className="tw-font-semibold tw-text-xs tw-text-gray-700 tw-mb-2 tw-flex tw-items-center tw-gap-1.5">
                                                <Building2 className="tw-w-3.5 tw-h-3.5" />
                                                {t("Laboratory Info")}
                                            </div>
                                            <div className="tw-space-y-1.5 tw-text-xs">
                                                <div className="tw-flex tw-justify-between">
                                                    <span className="tw-text-gray-600">{t("Lab Name")}:</span>
                                                    <span className="tw-font-medium tw-text-gray-900">{currentLab?.facility_name}</span>
                                                </div>
                                                <div className="tw-flex tw-justify-between tw-items-center">
                                                    <span className="tw-text-gray-600">{t("Base Currency")}:</span>
                                                    <span className="tw-font-bold tw-text-primary">{currentLab?.baseCurrency || "INR"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
                                    <button type="submit" disabled={isSubmitting} className="btn btn-primary tw-px-6">
                                        {isSubmitting ? t("Updating...") : t("Update User")}
                                    </button>
                                    <Link to={`${process.env.PUBLIC_URL}/user`}>
                                        <button type="button" className="btn btn-secondary">{t("Cancel")}</button>
                                    </Link>
                                </div>
                            </form>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}